import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

export function ContractUpdateCard({ onPublished }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/contract-update").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const publish = async () => {
    if (!window.confirm(`Pubblicare la versione ${d.proposed_version}? Tutti i dealer dovranno rifirmare con codice SMS entro ${d.resign_days} giorni.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/contract-update/publish", { reason });
      toast.success(`Versione ${data.version} pubblicata: ${data.notified} dealer avvisati`);
      load(); onPublished?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore pubblicazione"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 glow-border space-y-3" data-testid="contract-update-card">
      <p className="font-head font-semibold flex items-center gap-2"><FileSignature className="w-4 h-4 text-primary" /> Clausole inattività e «Presenta un dealer»</p>
      {d.has_clauses ? (
        <p className="text-sm flex items-start gap-2" data-testid="contract-update-active"><CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" /> Già nel contratto in vigore (versione {d.current_version}).{d.resign_deadline && ` I dealer devono rifirmare entro il ${fmt(d.resign_deadline)}.`}</p>
      ) : (<>
        <p className="text-sm text-muted-foreground" data-testid="contract-update-proposal">Pronta la versione <strong>{d.proposed_version}</strong>: aggiunge il programma inviti, la sospensione dopo 12 mesi senza utilizzo, la penale sul credito non usato dopo 24 mesi (con preavvisi via email) e l'approvazione specifica delle clausole. <strong>{d.dealers_to_resign}</strong> dealer dovranno rifirmare con SMS entro {d.resign_days} giorni; nel frattempo continuano a operare.</p>
        <button type="button" className="text-sm text-primary underline" onClick={() => setOpen((o) => !o)} data-testid="contract-update-toggle">{open ? "Nascondi le nuove clausole" : "Leggi le nuove clausole"}</button>
        {open && <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/20 p-4 text-xs font-sans" data-testid="contract-update-text">{d.added_text}</pre>}
        <p className="text-xs text-amber-500 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Testo scritto con cura, ma non sostituisce un parere legale: fallo rivedere al tuo avvocato prima di pubblicarlo.</p>
        <div className="flex flex-wrap items-center gap-3 justify-end">
          <Input className="max-w-sm" placeholder="Motivo (es. nuove regole inattività)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="contract-update-reason" />
          <Button disabled={busy || reason.trim().length < 3} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={publish} data-testid="contract-update-publish">Pubblica e chiedi la rifirma</Button>
        </div>
      </>)}
    </Card>
  );
}
